/**
 * PDF Export Button Component
 * Provides dropdown menu for various PDF export options
 */
import { useState } from 'react';
import { FileDown, ChevronDown, FileText, Users, DollarSign, Briefcase, Calendar, Loader2 } from 'lucide-react';
import {
  generateHRSummaryReport,
  generateAttendanceReport,
  generateFinanceSummaryReport,
  generatePayrollReport,
  generateAssetReport
} from '../utils/pdfExport';

const ExportButton = ({ type = 'all', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState('');

  const exportOptions = {
    all: [
      { id: 'finance', label: 'Laporan Kewangan', icon: DollarSign, fn: generateFinanceSummaryReport },
      { id: 'payroll', label: 'Laporan Gaji', icon: Users, fn: generatePayrollReport },
      { id: 'hr', label: 'Laporan HR', icon: Users, fn: generateHRSummaryReport },
      { id: 'attendance', label: 'Laporan Kehadiran', icon: Calendar, fn: generateAttendanceReport },
      { id: 'asset', label: 'Laporan Aset', icon: Briefcase, fn: generateAssetReport }
    ],
    hr: [
      { id: 'hr', label: 'Ringkasan HR', icon: Users, fn: generateHRSummaryReport },
      { id: 'attendance', label: 'Kehadiran Bulan Ini', icon: Calendar, fn: generateAttendanceReport },
      { id: 'payroll', label: 'Laporan Gaji', icon: DollarSign, fn: generatePayrollReport }
    ],
    finance: [
      { id: 'finance', label: 'Ringkasan Kewangan', icon: DollarSign, fn: generateFinanceSummaryReport },
      { id: 'payroll', label: 'Laporan Gaji', icon: Users, fn: generatePayrollReport },
      { id: 'asset', label: 'Laporan Aset', icon: Briefcase, fn: generateAssetReport }
    ],
    asset: [
      { id: 'asset', label: 'Senarai Aset', icon: Briefcase, fn: generateAssetReport }
    ]
  };

  const options = exportOptions[type] || exportOptions.all;

  const handleExport = async (option) => {
    setLoading(option.id);
    try {
      const result = await option.fn();
      if (!result.success) {
        alert('Gagal menjana PDF: ' + result.error);
      }
    } catch (error) {
      alert('Ralat: ' + error.message);
    } finally {
      setLoading('');
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        data-testid="export-pdf-btn"
      >
        <FileDown className="w-4 h-4" />
        <span>Export PDF</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border z-50 overflow-hidden" data-testid="export-dropdown">
            <div className="p-2 bg-gray-50 border-b">
              <p className="text-xs text-gray-500 font-medium">Pilih Jenis Laporan</p>
            </div>
            <div className="p-1">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleExport(option)}
                  disabled={loading !== ''}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading === option.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  ) : (
                    <option.icon className="w-4 h-4 text-gray-400" />
                  )}
                  <span>{option.label}</span>
                  {loading === option.id && (
                    <span className="ml-auto text-xs text-emerald-600">Menjana...</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportButton;
